import {Box, Grid, GridItem, Icon, SimpleGrid, useColorModeValue} from "@chakra-ui/react";
import React, {useEffect, useState} from "react";
import DashboardCard from "./DashboardCard.tsx";
import {MdLocalHospital, MdPerson} from "react-icons/md";
import IconBox from '../icons/IconBox.js'
import {useGetUsersQuery} from "../../store/userApi/userApiSlice.ts";
import {useGetPatientQuery} from "../../store/patientApi/patientApiSlice.ts";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/store.ts";
import MiniCalendar from '../calendar/MiniCalendar.jsx'
import WeeklyTraffic from '../analytics/WeeklyTraffic.jsx'
import MonthlyTraffic from '../analytics/MonthlyTraffic.jsx'
import {changeSearchValue} from "../../store/search/searchSlice.ts";

export default function DashboardContent() {
    // Chakra Color Mode
    const dispatch = useDispatch();
    dispatch(changeSearchValue(''))
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    let {data, isLoading: isUsersLoading} = useGetUsersQuery();
    let {data: patientData, isLoading: isPatientsLoading} = useGetPatientQuery({page: 1, page_size: 500})
    const user = useSelector((state: RootState) => state?.user?.user)
    const [weeklyChartData, setWeeklyChartData] = useState<any[]>([]);
    const [monthlyChartData, setMonthlyChartData] = useState<any[]>([]);

    useEffect(() => {
        if (!patientData) return;
        const resultWeekly = getLast7DaysPatientsCounts(patientData);
        const resultMonthly = getLast30DaysPatientsCounts(patientData);
        setWeeklyChartData(() => [
            {
                name: 'Patients',
                data: resultWeekly,
            },
        ]);
        setMonthlyChartData(() => [
            {
                name: 'Patients',
                data: resultMonthly,
            },
        ]);
    }, [patientData]);

    function getLast7DaysPatientsCounts(patientData) {
        const result = Array(7).fill(0);
        const today = new Date();

        patientData?.forEach((item) => {
            const createdDate = new Date(item.created_at);
            // Normalize both dates to midnight for comparison
            const diffTime = today.setHours(0, 0, 0, 0) - createdDate.setHours(0, 0, 0, 0);
            const diffDays = diffTime / (1000 * 60 * 60 * 24);

            // Count only if within the last 7 days (0 to 6)
            if (diffDays >= 0 && diffDays < 7) {
                const index = 6 - Math.floor(diffDays); // 6 = today, 0 = 6 days ago
                result[index]++;
            }
        })
        return result;
    }

    // function getLast30DaysPatientsCounts(patientData) {
    //     const result = Array(30).fill(0);
    //     const today = new Date();
    //
    //     patientData?.forEach((item) => {
    //         const createdDate = new Date(item.created_at);
    //         // Normalize both dates to midnight
    //         const diffTime = today.setHours(0, 0, 0, 0) - createdDate.setHours(0, 0, 0, 0);
    //         const diffDays = diffTime / (1000 * 60 * 60 * 24);
    //
    //         // Count only if within the last 30 days (0 to 29)
    //         if (diffDays >= 0 && diffDays < 30) {
    //             const index = 29 - Math.floor(diffDays); // 29 = today, 0 = 29 days ago
    //             result[index]++;
    //         }
    //     });
    //     return result;
    // }

    function getLast30DaysPatientsCounts(patientData) {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-based
        const currentDate = today.getDate();   // 1-based
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        // Full month initialized to 0
        const result = Array(daysInMonth).fill(0);

        patientData?.forEach((item) => {
            const createdDate = new Date(item.created_at);
            const year = createdDate.getFullYear();
            const month = createdDate.getMonth();
            const day = createdDate.getDate(); // 1-based

            if (
                year === currentYear &&
                month === currentMonth &&
                day <= currentDate
            ) {
                result[day - 1]++; // -1 to convert 1-based day to array index
            }
        });

        // Zero out future days explicitly (optional but safe)
        for (let i = currentDate; i < daysInMonth; i++) {
            result[i] = 0;
        }

        return result;
    }


    return (
        <Box pt={{base: "130px", md: "80px", xl: "80px"}}>
            <Grid
                templateColumns={{base: "1fr", xl: "1.15fr 1.15fr 1.7fr"}}
                templateRows={{base: "auto", xl: "auto auto"}}
                gap="20px"
                mb="20px"
            >
                {/* Patients Card */}
                <GridItem colSpan={1}>
                    <DashboardCard
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon w="32px" h="32px" as={MdPerson} color={brandColor}/>
                                }
                            />
                        }
                        heading="Patients"
                        count={patientData?.length}
                        link="/admin/patients"
                        isLoading={isPatientsLoading}
                    />
                </GridItem>

                {/* Doctors Card */}
                <GridItem colSpan={1}>
                    <DashboardCard
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon w="32px" h="32px" as={MdLocalHospital} color={brandColor}/>
                                }
                            />
                        }
                        heading="Doctors"
                        count={data?.users?.length}
                        link={user?.isSuperuser ? "/admin/doctors" : ""}
                        isLoading={isUsersLoading}
                    />
                </GridItem>

                {/* Calendar */}
                <GridItem rowSpan={2} colSpan={1}>
                    <MiniCalendar h="100%" minW="100%" selectRange={false}/>
                </GridItem>

                {/* Weekly Traffic */}
                <GridItem colSpan={2}>
                    <WeeklyTraffic
                        isLoading={weeklyChartData.length === 0}
                        chartData={weeklyChartData}
                    />
                </GridItem>
            </Grid>


            <SimpleGrid columns={{base: 1, md: 4, xl: 4}} gap="20px" mb="20px">
                <GridItem colSpan={{base: 1, md: 4, xl: 4}}>
                    <MonthlyTraffic
                        isLoading={monthlyChartData.length === 0}
                        chartData={monthlyChartData}
                    />
                </GridItem>
            </SimpleGrid>

        </Box>
    );
}
